import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { listRunningExperiments, type PublicExperiment } from "@/lib/experiments.functions";
import { assignVariant, trackImpression } from "@/lib/ab";

type Fallback = {
  label: string;
  blurb: string;
  image: string;
};

const experimentsQueryOptions = {
  queryKey: ["ab", "running-experiments"] as const,
  queryFn: () => listRunningExperiments(),
  staleTime: 60_000,
  refetchOnWindowFocus: false,
};

/**
 * Renders a lifestyle card. When an experiment with `experimentKey` is
 * running, picks (and sticks) a variant and renders its label/blurb/image.
 * Otherwise renders the hard-coded `fallback` — so the page looks identical
 * to the pre-experiment state.
 */
export function ExperimentCard({
  experimentKey,
  fallback,
}: {
  experimentKey: string;
  fallback: Fallback;
}) {
  const { data: experiments } = useQuery(experimentsQueryOptions);

  const experiment: PublicExperiment | undefined = useMemo(
    () => experiments?.find((e) => e.key === experimentKey),
    [experiments, experimentKey],
  );

  const variant = useMemo(() => {
    if (!experiment) return null;
    return assignVariant({
      id: experiment.id,
      key: experiment.key,
      variants: experiment.variants,
    });
  }, [experiment]);

  useEffect(() => {
    if (experiment && variant) {
      void trackImpression(experiment.id, variant.id);
    }
  }, [experiment, variant]);

  const label = variant?.label || fallback.label;
  const blurb = variant?.blurb || fallback.blurb;
  const image = variant?.image_url || fallback.image;

  return (
    <article className="group relative overflow-hidden rounded-2xl border border-border bg-card">
      <div className="aspect-[4/3] overflow-hidden bg-background">
        <img
          src={image}
          alt={label}
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </div>
      <div className="p-5">
        <h3 className="font-display text-lg font-semibold">{label}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{blurb}</p>
      </div>
    </article>
  );
}
